<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div
			class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg
						class="w-5 h-5 text-primary-400"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Organization Management
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Create a new organization
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<form @submit.prevent="submitForm" class="space-y-4 mt-2">
			<!-- ══ CARD — Logo + Name ══ -->
			<div
				class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-5 md:p-6">
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-5">
					Organization Details
				</p>

				<div class="flex flex-col md:flex-row gap-6">
					<!-- Logo Uploader -->
					<div
						class="flex flex-col justify-center items-center rounded-xl w-full md:w-64 h-52 border-2 border-dashed cursor-pointer transition-all duration-200 border-primary-200 dark:border-dark-500 hover:border-primary-400 dark:hover:border-primary-400 bg-primary-50/50 dark:bg-dark-600/50"
						@dragover.prevent
						@drop.prevent="handleDrop">
						<template v-if="!organization.logoURL">
							<div
								class="w-12 h-12 rounded-xl bg-primary-100 dark:bg-dark-500 flex items-center justify-center mb-3">
								<svg
									class="w-6 h-6 text-primary-400"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									viewBox="0 0 24 24">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1M16 12l-4-4m0 0l-4 4m4-4v12" />
								</svg>
							</div>
							<p
								class="text-[13px] text-muted dark:text-muted-light text-center leading-relaxed">
								Drop logo to upload<br />or
								<span
									class="text-primary-400 font-semibold cursor-pointer hover:text-primary-600"
									@click="browseFiles"
									>browse</span
								>
							</p>
						</template>
						<template v-else>
							<img
								:src="organization.logoURL"
								alt="Preview"
								class="w-full h-40 object-cover rounded-lg" />
							<button
								@click="resetImage"
								class="mt-2 text-[12px] font-semibold text-red-400 hover:text-red-600 transition-colors">
								Remove
							</button>
						</template>
						<input
							type="file"
							ref="fileInput"
							class="hidden"
							@change="handleImageUpload" />
					</div>

					<!-- Name -->
					<div class="flex-1">
						<label class="form-label">
							<svg
								class="w-4 h-4"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" />
							</svg>
							Organization Name
						</label>
						<input
							v-model="organization.name"
							type="text"
							class="form-input"
							:class="{
								'border-red-400': errors.name,
								'border-primary-400': !errors.name && organization.title,
							}" />
						<ErrorMessage :message="errors.name" v-if="errors.name" />
					</div>
				</div>
			</div>

			<div class="form-divider" />

			<!-- ── Submit ── -->
			<button
				type="submit"
				:disabled="isSubmitting || !isValidForm"
				class="w-full font-display font-bold text-[14px] py-3.5 rounded-xl bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
				<span v-if="!isLoading" class="flex items-center justify-center gap-2">
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2.5"
							d="M5 13l4 4L19 7" />
					</svg>
					Save Organization
				</span>
				<span v-else class="flex items-center justify-center gap-2">
					<svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4" />
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
					</svg>
					Processing...
				</span>
			</button>
		</form>
	</div>
</template>

<script>
import { ref, reactive } from "vue";
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import ErrorMessage from "@/components/messages/ErrorMessage.vue";

export default {
	name: "CreateOrganization",
	components: { Breadcrumbs, ErrorMessage },
	setup() {
		const isSubmitting = ref(false);
		const isValidForm = ref(true);
		const isLoading = ref(false);
		const organization = reactive({
			name: "",
			email: "",
			phone: "",
			description: "",
			logo: null,
			logoURL: "",
		});
		const errors = reactive({
			name: "",
			email: "",
			phone: "",
			description: "",
		});
		const fileInput = ref(null);
		const selectedFiles = ref([]);

		const browseFiles = () => {
			if (fileInput.value) fileInput.value.click();
		};
		const handleFileChange = (e) => {
			selectedFiles.value = Array.from(e.target.files);
		};
		const handleDrop = (e) => {
			const file = e.dataTransfer.files[0];
			if (!file) return;
			handleImageUpload({ target: { files: [file] } });
		};
		const cancelUpload = () => {
			selectedFiles.value = [];
			fileInput.value.value = null;
		};
		const handleImageUpload = (e) => {
			const file = e.target.files[0];
			if (!file.type.startsWith("image/")) {
				alert("Please upload an image file.");
				return;
			}
			if (file.size > 5 * 1024 * 1024) {
				alert("File size exceeds 5MB.");
				return;
			}
			organization.logo = file;
			organization.logoURL = URL.createObjectURL(file);
		};
		const resetImage = () => {
			if (organization.logoURL) URL.revokeObjectURL(organization.logoURL);
			organization.logo = null;
			organization.logoURL = "";
		};
		const validateForm = () => {};
		const submitForm = async () => {
			if (!validateForm()) {
				console.log("Form invalid");
				return;
			}
		};

		return {
			organization,
			isSubmitting,
			isValidForm,
			isLoading,
			errors,
			handleImageUpload,
			resetImage,
			browseFiles,
			handleDrop,
			cancelUpload,
			handleFileChange,
			fileInput,
			submitForm,
		};
	},
};
</script>

<style scoped>
.form-label {
	@apply flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest mb-2
         text-muted dark:text-muted-light;
}
.form-input {
	@apply w-full px-3.5 py-2.5 rounded-xl text-[13px] font-medium
         bg-primary-50/60 dark:bg-dark-600
         border border-primary-100 dark:border-dark-500
         text-dark-700 dark:text-white
         placeholder-muted dark:placeholder-muted-light
         outline-none transition-all duration-200
         focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20
         dark:focus:border-primary-400;
}
.form-divider {
	@apply flex items-center gap-3 my-1;
}
.form-divider::before,
.form-divider::after {
	content: "";
	@apply flex-1 border-t border-dashed border-primary-100 dark:border-dark-600;
}
</style>
